import { Box, Typography } from '@mui/material';

import { cx } from '@/styles/muiTheme';

import PlayerAvatar from './PlayerAvatar';
import TeamCrest from './TeamCrest';

type Props = {
  playerNumber: string;
  playerName: string;
  playerPosition: string;
  headshotUrl: string | null;
  teamAbbreviation: string;
  teamLogoUrl: string | null;
  teamColor: string;
  /** Brightens the text only — the row itself never paints a background. */
  selected?: boolean;
};

const AVATAR = 24;
/** Not smaller: TeamCrest's initials fallback centres a 9px three-letter
 *  abbreviation with no padding, and it overflows the disc below ~18. */
const CREST = 18;
/** How far the crest sits over the headshot's right edge. */
const OVERLAP = 6;

/**
 * A tracked player as a single line: headshot paired with the team crest, then
 * jersey, name and position.
 *
 * This is the background-free counterpart to `features/teams`' `TrackedItem`.
 * That one paints its own `cx.bg.surface` card and inverts to `cx.light[200]`
 * when selected, which is right for the game page's standalone list but wrong
 * inside an Autocomplete: the `<li>` already owns the hover, keyboard-focus and
 * aria-selected backgrounds, and an opaque child hides all three. Here the row
 * draws nothing and lets the option own the surface, so `selected` only has to
 * lift the text.
 *
 * Built from the sibling `PlayerAvatar` / `TeamCrest` rather than MUI Avatars so
 * the missing-headshot and missing-crest fallbacks match the cards and group
 * headers on the same page.
 */
const TrackedPlayerRow = ({
  playerNumber,
  playerName,
  playerPosition,
  headshotUrl,
  teamAbbreviation,
  teamLogoUrl,
  teamColor,
  selected = false,
}: Props) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', minWidth: 0 }}>
    {/* Sized to the overlapped pair so the text column starts at the same x on
        every row, whether or not a crest resolved. */}
    <Box
      sx={{
        position: 'relative',
        width: AVATAR + CREST - OVERLAP,
        height: AVATAR,
        flexShrink: 0,
      }}
    >
      <PlayerAvatar name={playerName} headshotUrl={headshotUrl} size={AVATAR} />
      {/* Bottom-aligned rather than centred: crests are wordmarks as often as
          roundels, and a centred one collides with the face. */}
      <Box sx={{ position: 'absolute', right: 0, bottom: 0, lineHeight: 0 }}>
        <TeamCrest
          abbreviation={teamAbbreviation}
          logoUrl={teamLogoUrl}
          color={teamColor}
          size={CREST}
        />
      </Box>
    </Box>

    {/* Fixed width so the names line up into a column across rows. */}
    <Typography
      sx={{
        fontSize: 12,
        width: 28,
        flexShrink: 0,
        color: selected ? cx.text.primary : cx.text.tertiary,
      }}
    >
      {playerNumber ? `#${playerNumber}` : ''}
    </Typography>

    <Typography
      sx={{
        fontSize: 12,
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: selected ? cx.text.primary : cx.text.secondary,
      }}
    >
      {playerName}
    </Typography>

    <Typography sx={{ fontSize: 11, flexShrink: 0, color: cx.text.tertiary }}>
      {playerPosition}
    </Typography>
  </Box>
);

export default TrackedPlayerRow;
